/**
 * Friendly formatting for the chat's error / stop / rate-limit
 * surfaces.
 *
 * The workspace server, the SDK, and the upstream Anthropic API all
 * speak in lowercase enum codes (``authentication_failed``,
 * ``max_output_tokens``, ``five_hour``) and raw exception strings.
 * Rendering those verbatim makes the chat feel like debug output.
 *
 * Every error surface in the agent panel — the inline turn-tail
 * pill, the crashed-session full-pane, the rate-limit banner —
 * routes through one of the formatters here and gets back a small
 * display object the UI knows how to render: a Lucide icon, a
 * sentence-case headline, an optional one-line recovery hint, and
 * a tone band. The raw underlying string is preserved as
 * ``raw`` so we can still expose it on hover for debugging without
 * letting it own the headline real estate.
 */

import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  CircleSlash,
  Clock,
  CreditCard,
  KeyRound,
  MessageSquareText,
  Pause,
  ServerCrash,
  ShieldAlert,
  WifiOff,
} from 'lucide-react';
import type { ConnectionStatus } from './reducer';
import type { AssistantMessageErrorType, RateLimitInfo, StopReason } from './types';

/**
 * Visual band a notice gets rendered in. Maps to a colour family
 * the renderer applies — keeps the design system in sync without
 * letting individual call sites pick colours.
 */
export type NoticeTone = 'error' | 'warning' | 'neutral';

export interface AgentNotice {
  /** Lucide icon paired with the headline. */
  icon: LucideIcon;
  /** Short, sentence-case headline. */
  headline: string;
  /** One-line follow-up — what to do, what it means. Optional. */
  hint?: string;
  /** Visual band: ``error`` for red, ``warning`` for amber, ``neutral`` for muted gray. */
  tone: NoticeTone;
  /**
   * Original underlying message. Renderers expose it as a
   * ``title`` attribute (or a "Details" expand) so power users can
   * still see the raw error without it dominating the chat.
   */
  raw?: string;
}

// ---------------------------------------------------------------------------
// Turn-level: assistant frame's ``error`` field
// ---------------------------------------------------------------------------

/**
 * Format the value found on :attr:`Turn.error` (i.e. the upstream
 * ``AssistantMessageErrorType`` plus an optional free-form
 * fallback). Returns a notice ready to drop into a turn-tail pill.
 *
 * :param error: The raw value from ``turn.error`` — typed code,
 *     free-form string, ``null``, or ``undefined``.
 * :return: A formatted notice, or ``null`` when there's no error
 *     to render.
 */
export function formatTurnError(error: AssistantMessageErrorType | string | null | undefined): AgentNotice | null {
  if (error === null || error === undefined) return null;
  const trimmed = typeof error === 'string' ? error.trim() : '';
  if (!trimmed) return null;

  switch (trimmed) {
    case 'authentication_failed':
      return {
        icon: KeyRound,
        headline: 'Authentication failed',
        hint: 'The workspace server rejected the request. Re-authenticate, then send the message again.',
        tone: 'error',
      };
    case 'rate_limit':
      return {
        icon: Clock,
        headline: 'Rate limit reached',
        hint: 'Wait a moment before sending the next message.',
        tone: 'warning',
      };
    case 'billing_error':
      return {
        icon: CreditCard,
        headline: 'Billing issue',
        hint: "Check the workspace's API quota or payment method.",
        tone: 'error',
      };
    case 'invalid_request':
      return {
        icon: AlertTriangle,
        headline: 'Invalid request',
        hint: 'The model rejected the request shape. The chat is still alive — try rephrasing.',
        tone: 'error',
      };
    case 'server_error':
      return {
        icon: ServerCrash,
        headline: 'Upstream server error',
        hint: 'The Anthropic API returned an error. Try again in a moment.',
        tone: 'error',
      };
    case 'max_output_tokens':
      return {
        icon: MessageSquareText,
        headline: 'Response cut off at length limit',
        hint: 'Ask the agent to continue from where it stopped.',
        tone: 'warning',
      };
    case 'unknown':
      return {
        icon: AlertCircle,
        headline: 'Something went wrong',
        hint: 'The workspace server reported an unspecified error.',
        tone: 'error',
      };
    default:
      if (_isAuthFailure(trimmed)) {
        return {
          icon: KeyRound,
          headline: 'Authentication failed',
          hint: 'Your session token may be stale. Run `antioch login`, then send the message again.',
          tone: 'error',
          raw: trimmed,
        };
      }
      return {
        icon: AlertCircle,
        headline: 'Agent error',
        hint: capitalize(trimmed),
        tone: 'error',
        raw: trimmed,
      };
  }
}

// ---------------------------------------------------------------------------
// Turn-level: assistant frame's ``stop_reason`` (when not normal end)
// ---------------------------------------------------------------------------

/**
 * Format a non-trivial ``stop_reason`` — i.e. anything other than
 * ``end_turn`` / ``tool_use`` which the chat treats as the normal
 * end of a turn.
 *
 * :param stop: The raw value from ``turn.stopReason``.
 * :return: A formatted notice, or ``null`` for normal ends.
 */
export function formatStopReason(stop: StopReason | string | null | undefined): AgentNotice | null {
  if (stop === null || stop === undefined) return null;
  if (stop === 'end_turn' || stop === 'tool_use') return null;

  switch (stop) {
    case 'max_tokens':
      return {
        icon: MessageSquareText,
        headline: 'Hit token limit',
        hint: 'Response was truncated. Ask the agent to continue.',
        tone: 'warning',
      };
    case 'pause_turn':
      return {
        icon: Pause,
        headline: 'Turn paused',
        hint: 'The agent paused mid-turn. Send a follow-up to resume.',
        tone: 'neutral',
      };
    case 'refusal':
      return {
        icon: ShieldAlert,
        headline: 'Request declined',
        hint: 'The model declined to respond.',
        tone: 'warning',
      };
    case 'stop_sequence':
      return {
        icon: CircleSlash,
        headline: 'Stopped on sequence',
        tone: 'neutral',
      };
    default:
      return {
        icon: CircleSlash,
        headline: 'Turn stopped',
        hint: capitalize(stop),
        tone: 'neutral',
        raw: stop,
      };
  }
}

// ---------------------------------------------------------------------------
// Session-level: full-pane crash screen
// ---------------------------------------------------------------------------

/**
 * Format the inputs that trigger the SessionCrash full-pane —
 * either the latest ``server_error`` frame's message or the WS
 * being in a terminal ``error`` state.
 *
 * Auth-shaped messages get the ``KeyRound`` treatment with a
 * ``antioch login`` nudge; connection drops get ``WifiOff`` with
 * a "server may be restarting" line; everything else falls back
 * to the generic crashed-session notice. The raw error is always
 * preserved on ``raw``.
 *
 * :param message: The last ``server_error`` frame's payload, or
 *     ``null`` when none.
 * :param connectionStatus: The transport's current status — when
 *     it's ``error``, that's our fallback signal.
 * :return: A formatted notice, or ``null`` when neither input
 *     indicates a fatal state.
 */
export function formatSessionCrash(
  message: string | null | undefined,
  connectionStatus: ConnectionStatus
): AgentNotice | null {
  const trimmed = (message ?? '').trim();
  const isFatal = trimmed.length > 0 || connectionStatus === 'error';
  if (!isFatal) return null;

  // Auth-shaped server errors. Match on the canonical strings the
  // workspace server / Anthropic API produce so an upstream wording
  // change doesn't silently demote the message back to the generic
  // crash.
  if (trimmed && _isAuthFailure(trimmed)) {
    return {
      icon: KeyRound,
      headline: 'Authentication failed',
      hint: 'Your session token may be stale. Run `antioch login` and click Retry.',
      tone: 'error',
      raw: trimmed,
    };
  }

  // Process-level crashes — checked before the connection branch
  // because the server's task pump prefixes its payload with
  // "agent session crashed — {ExceptionType}: {message}". When the
  // wrapped exception happens to contain transport language ("..."
  // ConnectionError, WebSocketException, …) the crash framing is
  // still the more specific signal: the user wants to know the
  // session crashed, with the connection error as the cause. We
  // strip the redundant prefix so the hint shows just the detail.
  if (/crash|killed|exited|terminated|panic/i.test(trimmed)) {
    const headline = 'Agent session crashed';
    const designed = 'The agent process exited. Click Retry to start a fresh session — your scrollback is preserved.';
    return {
      icon: ServerCrash,
      headline,
      hint: _hintFromRaw(headline, trimmed, designed),
      tone: 'error',
      raw: trimmed,
    };
  }

  // Connection drops — either the WS reached its terminal error
  // state or the message itself smells like a transport failure
  // (without the "crashed" framing handled above).
  if (connectionStatus === 'error' || /websocket|connection|network|disconnect/i.test(trimmed)) {
    return {
      icon: WifiOff,
      headline: 'Lost connection to the workspace server',
      hint: 'The workspace server may be restarting. Retry in a moment — your scrollback is preserved.',
      tone: 'error',
      raw: trimmed || undefined,
    };
  }

  // Generic fallback — surface the raw message when it carries
  // information the headline doesn't (capitalized so it doesn't
  // look like dropped-in machine output), and fall back to a
  // designed recovery line when the server's string is just a
  // restatement of "session ended unexpectedly".
  const headline = 'Session ended unexpectedly';
  const designed = 'The chat has been disconnected. Click Retry to reconnect.';
  return {
    icon: AlertOctagon,
    headline,
    hint: trimmed ? _hintFromRaw(headline, trimmed, designed) : designed,
    tone: 'error',
    raw: trimmed || undefined,
  };
}

// ---------------------------------------------------------------------------
// Rate-limit banner
// ---------------------------------------------------------------------------

/**
 * Format a rejected :class:`RateLimitInfo` for the inline banner.
 * ``allowed`` / ``allowed_warning`` statuses don't surface as
 * banners — they're just signals to the request gate.
 *
 * The banner copy keys off ``rate_limit_type`` so the user sees
 * "Five-hour limit reached" rather than the generic enum, and
 * folds in the ``resets_at`` timestamp when present.
 *
 * :param info: The current rate-limit snapshot from the reducer.
 * :return: A formatted notice, or ``null`` when not a rejection.
 */
export function formatRateLimit(info: RateLimitInfo | null | undefined): AgentNotice | null {
  if (info?.status !== 'rejected') return null;

  const headline = _rateLimitHeadline(info.rate_limit_type);
  const resetSuffix = _formatResetSuffix(info.resets_at);
  const hint = resetSuffix
    ? `Resets ${resetSuffix}. The chat stays open — you just can't send new messages until it does.`
    : "The chat stays open — you just can't send new messages until the limit resets.";

  return {
    icon: Clock,
    headline,
    hint,
    tone: 'warning',
  };
}

/**
 * Map a ``rate_limit_type`` to a sentence-case headline.
 */
function _rateLimitHeadline(type: RateLimitInfo['rate_limit_type']): string {
  switch (type) {
    case 'five_hour':
      return 'Five-hour limit reached';
    case 'seven_day':
      return 'Weekly limit reached';
    case 'seven_day_opus':
      return 'Weekly Opus limit reached';
    case 'seven_day_sonnet':
      return 'Weekly Sonnet limit reached';
    case 'overage':
      return 'Account credit exhausted';
    default:
      return 'Rate limit reached';
  }
}

/**
 * Format a "resets in/at" suffix for rate-limit hints. Returns an
 * empty string when the timestamp is missing or in the past.
 *
 * Times within the next hour render as ``"in N min"``; further
 * out as ``"at HH:MM"`` so the user can read the wall-clock at a
 * glance.
 */
function _formatResetSuffix(resetsAtMs: number | null | undefined): string {
  if (!resetsAtMs) return '';
  const now = Date.now();
  const deltaMs = resetsAtMs - now;
  if (deltaMs <= 0) return '';
  const deltaMin = Math.round(deltaMs / 60_000);
  if (deltaMin < 60) {
    return deltaMin <= 1 ? 'in <1 min' : `in ${deltaMin} min`;
  }
  const dt = new Date(resetsAtMs);
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `at ${hh}:${mm}`;
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

/**
 * Capitalize the first letter of ``s`` without otherwise touching it.
 */
function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Detect auth-shaped errors from enum codes and free-form payloads.
 */
function _isAuthFailure(message: string): boolean {
  return /401|invalid bearer|authentication|unauthor/i.test(message);
}

/**
 * Compute the visible hint for a session-crash notice given the
 * server's raw payload, the chosen headline, and a designed
 * fallback line.
 *
 * Three branches, in order:
 *
 *  1. ``raw`` is just a case- / punctuation-different restatement
 *     of ``headline`` ("agent session crashed" vs "Agent session
 *     crashed"). Use the designed recovery copy — echoing the
 *     title would read as the same sentence twice.
 *  2. ``raw`` *starts with* the headline tokens, then carries a
 *     real detail tail ("agent session crashed — RuntimeError:
 *     connection closed"). Strip the prefix; the tail is the
 *     useful payload. If the strip leaves nothing (e.g. just a
 *     trailing colon), fall back to the designed copy.
 *  3. ``raw`` is a free-form message that doesn't restate the
 *     headline. Capitalize the first letter so it looks like
 *     prose and use it directly.
 *
 * The original ``raw`` is always preserved by the caller on
 * ``AgentNotice.raw`` so power users can see the full server
 * payload on hover regardless of which branch fires.
 */
function _hintFromRaw(headline: string, raw: string, designed: string): string {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const normHeadline = norm(headline);
  const normRaw = norm(raw);
  if (normHeadline === normRaw) return designed;
  if (normRaw.startsWith(`${normHeadline} `)) {
    // Build a tolerant regex that matches the headline tokens at
    // the start of ``raw``, allowing any non-word separators
    // between them and consuming any trailing separator (em-dash,
    // colon, period, newline, …) so the tail starts cleanly.
    const tokens = headline.split(/\s+/).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`^${tokens.join('\\W+')}\\W+`, 'i');
    const stripped = raw.replace(re, '').trim();
    return stripped ? capitalize(stripped) : designed;
  }
  return capitalize(raw);
}
